import React, { useState } from "react";
import { HotelInitial } from "@/types";
import LazyLoad from "../../image/LazyLoad";
import { motion, AnimatePresence } from "framer-motion";
import {
  formatPrice,
  roundUpToWholeNumber,
  formatImageUrl,
} from "@/lib/general";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";

interface HotelProps {
  listing: HotelInitial;
  handleAddToTrip: (selectedHotel: HotelInitial) => void;
  handleModalOpenMainLodging: (viewLodging: HotelInitial) => void;
  isAdmin: boolean;
}

const HotelCard: React.FC<HotelProps> = ({
  listing,
  handleAddToTrip,
  handleModalOpenMainLodging,
  isAdmin,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = async () => {
    setIsLiked(!isLiked);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 3000);
  };

  return (
    <div className="w-full rounded-md">
      <div className="relative w-full">
        <button
          onClick={() => handleModalOpenMainLodging(listing)}
          className="rounded-md h-48 w-full overflow-hidden relative"
        >
          <LazyLoad
            src={formatImageUrl(listing.imageURL, "high")}
            lowResSrc={formatImageUrl(listing.imageURL, "low")}
            alt={listing.hotelName}
            className="w-full h-full object-cover"
          />
        </button>

        {/* Price overlay */}
        <div className="absolute top-2 left-2 flex flex-row items-center space-x-1 bg-white/80 backdrop-blur-md rounded-md px-2 py-1">
          <p className="text-xs font-light text-neutral-900">
            {formatPrice(
              listing.currency,
              roundUpToWholeNumber(listing.dailyRate)
            )}
          </p>
        </div>

        {/* Like button */}
        <div className="group z-10 absolute right-3 top-2 flex items-center justify-center bg-stone-100 w-[27px] h-[27px] rounded-full">
          <button
            onClick={handleLike}
            className="group relative flex items-center justify-center w-full h-full"
          >
            <AnimatePresence>
              {isLiked && (
                <motion.i
                  key="filled-heart"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="text-[14px] text-red-600 absolute"
                >
                  <HeartIconSolid className="w-4 h-4" />
                </motion.i>
              )}
            </AnimatePresence>
            <motion.div
              animate={isLiked ? { scale: 0 } : { scale: 1 }}
              className={`${
                isLiked ? "text-transparent" : "text-gray-600"
              } transition-colors duration-300`}
            >
              <HeartIcon className="w-4 h-4" />
            </motion.div>
            {isAnimating && (
              <motion.div
                initial={{ scale: 0.8, opacity: 1 }}
                animate={{ scale: 1.3, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 bg-red-500/75 rounded-full"
              />
            )}
          </button>
        </div>

        {/* Flying hearts animation */}
        <AnimatePresence>
          {isAnimating && (
            <div className="absolute right-3 top-2 w-[35px] h-[35px] overflow-visible pointer-events-none">
              {[...Array(5)].map((_, index) => (
                <motion.i
                  key={index}
                  initial={{
                    opacity: 1,
                    scale: 0,
                    x: "50%",
                    y: "50%",
                  }}
                  animate={{
                    opacity: 0,
                    scale: 1.5,
                    x: `${100 + (Math.random() - 0.5) * 100}%`,
                    y: `${-50 - Math.random() * 100}%`,
                  }}
                  transition={{
                    duration: 1.5 + Math.random() * 1,
                    ease: "easeOut",
                    opacity: { duration: 2 },
                  }}
                  className="fa-solid fa-heart text-red-600 text-sm absolute"
                  style={{
                    originX: "50%",
                    originY: "50%",
                  }}
                />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <div className="flex w-full flex-row items-center justify-between pt-0.5 pb-3">
        <div
          className={`flex flex-col items-start justify-start space-y-1 w-full`}
        >
          <div className="w-full">
            <h3 className="text-[15px] w-full font-[500] tracking-tight text-neutral-800">
              {truncateTitle(listing.hotelName, 32)}
            </h3>
          </div>
          <div className="w-full flex flex-row items-center justify-start space-x-2 pr-1">
            <div className="flex flex-row items-center space-x-1.5 w-full pt-1">
              <div className="py-1 bg-indigo-50 flex flex-row items-center space-x-2 px-2 text-indigo-600 rounded-md">
                <i className="fa-solid fa-star text-[11px]"></i>
                <p className="text-[13px] font-light">{listing.reviewScore}</p>
              </div>
              <p className="pl-0.5 text-[12px] font-light text-neutral-500">
                {formatReviewCount(listing.reviewCount)} reviews
              </p>
            </div>
            {isAdmin && (
              <button
                onClick={() => handleAddToTrip(listing)}
                className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-indigo-800 hover:bg-indigo-700 font-medium transition-all duration-500 ease-in-out text-white"
              >
                <i className="fa-solid fa-plus text-xs"></i>
              </button>
            )}
            <button
              onClick={() => handleModalOpenMainLodging(listing)}
              className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 font-medium transition-all duration-500 ease-in-out text-neutral-800"
            >
              <p className="text-xs font-bold italic">i</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatReviewCount = (count: number) => {
  if (count < 1000) {
    return count;
  } else {
    return (count / 1000).toFixed(1) + "k";
  }
};

const truncateTitle = (title: string, maxLength: number) => {
  if (title.length > maxLength) {
    return title.slice(0, maxLength) + "...";
  }
  return title;
};

const ExtrasComponent: React.FC<{
  freeWifi: boolean;
  includeBreakfast: boolean;
}> = ({ freeWifi, includeBreakfast }) => {
  return (
    <div className="flex flex-row items-center text-neutral-600 ml-1">
      {freeWifi && (
        <div className="flex items-center">
          <i className="fa-regular fa-wifi text-[11px] mr-2"></i>
          <p className="text-xs font-light">
            {freeWifi ? "Free Wi-Fi" : "Wi-Fi"}
          </p>
        </div>
      )}
      {freeWifi && includeBreakfast && (
        <div className="flex items-center">
          <div className="w-1 h-1 bg-neutral-700 rounded-full mx-3"></div>
          <i className="fa-regular fa-coffee-pot text-[11px] mr-2"></i>
          <p className="text-xs font-light">
            {includeBreakfast ? "Includes Breakfast" : "No Breakfast"}
          </p>
        </div>
      )}
    </div>
  );
};

const DiscountComponent: React.FC<{
  currency: string;
  dailyRate: number;
  discountPercentage: number;
}> = ({ currency, dailyRate, discountPercentage }) => {
  const notionalAmountOffInCurrency = Math.round(
    (dailyRate * (100 - discountPercentage)) / 100
  );
  return (
    <div className="flex flex-row items-center bg-green-100 text-green-800 rounded-md px-2 py-1 text-[11px] font-semibold">
      <i className="fa-solid fa-money-bill-wave text-sm mr-2"></i>
      <p className="text-xs font-light">
        {formatPrice(currency, notionalAmountOffInCurrency)} off per night
      </p>
    </div>
  );
};

const PriceComponent: React.FC<{
  currency: string;
  price: number;
  crossedOutRate: number;
}> = ({ currency, price, crossedOutRate }) => {
  return (
    <div className="flex flex-row space-x-2 items-center ml-0.5">
      {crossedOutRate > 0 && (
        <div className="flex flex-row items-center justify-start space-x-2 w-">
          <p className="text-[14px] text-neutral-800 opacity-50 font-semibold line-through">
            {formatPrice(currency, roundUpToWholeNumber(crossedOutRate))}
          </p>
          <p className="text-[18px] text-neutral-800 font-semibold">
            {formatPrice(currency, roundUpToWholeNumber(price))}
          </p>
        </div>
      )}
      {crossedOutRate === 0 && (
        <p className="text-[18px] text-neutral-800 font-semibold">
          {formatPrice(currency, roundUpToWholeNumber(price))}
        </p>
      )}
      <p className="text-xs font-light text-neutral-400">p / night</p>
    </div>
  );
};

export default HotelCard;
